import { useEffect, useState, useCallback, useMemo } from "react";
import { Search, ChevronDown, ChevronUp, Mail, MapPin, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { get, put, post, inputCls, btnDark, btnLight, Card, StatusBadge, STATUS, chf, fmtDate, Empty, monthKey } from "./shared";

const OrderDetail = ({ o, onChanged }) => {
  const [status, setStatus] = useState(o.status);
  const [tracking, setTracking] = useState(o.tracking_number || "");
  const [carrier, setCarrier] = useState(o.carrier || "swisspost");
  const [note, setNote] = useState(o.internal_note || "");
  const [busy, setBusy] = useState(false);
  const terminal = ["cancelled", "refunded"].includes(o.status);
  const statusOptions = useMemo(() => Object.entries(STATUS).filter(([k]) => k !== "refunded" || status === "refunded"), [status]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await put(`/orders/${o.id}/status`, { status, tracking_number: tracking, carrier });
      if (note !== (o.internal_note || "")) await put(`/orders/${o.id}/note`, { note });
      toast.success(`${o.order_number} mis à jour${r.email_sent ? " · email d'expédition envoyé" : ""}`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de mise à jour"); }
    finally { setBusy(false); }
  };

  const refund = async () => {
    if (!window.confirm(`Rembourser intégralement ${chf(o.total)} à ${o.customer_email} via Stripe ?\nLe stock sera réintégré.`)) return;
    setBusy(true);
    try {
      await post(`/orders/${o.id}/refund`);
      toast.success("Remboursement Stripe effectué");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Remboursement impossible"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border-t border-stone-100 px-5 py-5 grid lg:grid-cols-2 gap-6" data-testid={`order-detail-${o.order_number}`}>
      <div className="space-y-4 text-sm">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-wide text-stone-400 mb-1">Articles</p>
          <ul className="divide-y divide-stone-100">
            {o.items.map((it) => (
              <li key={it.product_id || it.lookup_key || it.name} className="py-1.5 flex justify-between"><span>{it.name} × {it.quantity}</span><span className="font-mono text-stone-600">{chf((it.unit_amount * it.quantity) / 100)}</span></li>
            ))}
          </ul>
          <div className="mt-2 pt-2 border-t border-stone-200 flex justify-between font-mono"><span>Total payé (TVA incl.)</span><span>{chf(o.total)}</span></div>
          {o.discount > 0 && <p className="text-xs text-stone-500 mt-1">Remise appliquée : −{chf(o.discount)} ({o.promo_code})</p>}
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-wide text-stone-400 mb-1">Client</p>
          <p className="flex items-center gap-2"><Mail size={13} className="text-stone-400" /> {o.customer_name || "—"} · <a href={`mailto:${o.customer_email}`} className="font-mono text-xs hover:text-[#C85A32]">{o.customer_email}</a></p>
          <p className="flex items-start gap-2 mt-1 text-stone-600"><MapPin size={13} className="text-stone-400 mt-0.5" /> {o.customer_address || "Adresse : voir le Dashboard Stripe (commande antérieure)"}{o.customer_phone ? ` · ${o.customer_phone}` : ""}</p>
          {o.customer_note && <p className="mt-1 text-xs text-stone-500 italic">Remarque client : {o.customer_note}</p>}
          {o.has_preorder && <p className="mt-1 text-[10px] font-mono uppercase text-sky-700">Contient une précommande</p>}
        </div>
        <div className="text-xs text-stone-400 font-mono space-y-0.5">
          <p>Session Stripe : {o.session_id}</p>
          {o.refund_id && <p>Remboursement : {o.refund_id} · {fmtDate(o.refunded_at, true)}</p>}
          {o.shipped_at && <p>Expédiée le {fmtDate(o.shipped_at, true)}</p>}
        </div>
      </div>

      <div className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs text-stone-500">Statut
            <select data-testid={`admin-order-status-${o.order_number}`} value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} w-full mt-1`} disabled={terminal}>
              {statusOptions.map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </label>
          <label className="text-xs text-stone-500">Transporteur
            <select data-testid={`admin-order-carrier-${o.order_number}`} value={carrier} onChange={(e) => setCarrier(e.target.value)} className={`${inputCls} w-full mt-1`} disabled={terminal}>
              {[["swisspost", "La Poste Suisse"], ["dhl", "DHL"], ["dpd", "DPD"], ["ups", "UPS"], ["planzer", "Planzer"], ["other", "Autre"]].map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          <label className="text-xs text-stone-500 sm:col-span-2">N° de suivi (celui de l'étiquette imprimée chez le transporteur)
            <input data-testid={`admin-order-tracking-${o.order_number}`} value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="99.00.123456.78901234" className={`${inputCls} w-full mt-1`} disabled={terminal} />
            {o.tracking_url && <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-[11px] text-[#C85A32] underline">Ouvrir le suivi transporteur</a>}
          </label>
        </div>
        <label className="text-xs text-stone-500 block">Note interne (invisible pour le client)
          <textarea data-testid={`admin-order-note-${o.order_number}`} value={note} onChange={(e) => setNote(e.target.value)} rows={2} className={`${inputCls} w-full mt-1`} placeholder="Ex. : client a demandé un emballage cadeau" />
        </label>
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button data-testid={`admin-order-save-${o.order_number}`} onClick={save} disabled={busy} className={btnDark}><Save size={13} /> Enregistrer</button>
          {!terminal && o.type !== "subscription" && (
            <button data-testid={`admin-order-refund-${o.order_number}`} onClick={refund} disabled={busy} className={`${btnLight} hover:!border-rose-600 hover:!text-rose-700`}><RotateCcw size={13} /> Rembourser (Stripe)</button>
          )}
          {status === "shipped" && o.status !== "shipped" && <span className="text-[11px] text-stone-500">→ un email avec le suivi sera envoyé au client</span>}
        </div>
      </div>
    </div>
  );
};

const OrderRow = ({ o, onChanged }) => {
  const [open, setOpen] = useState(false);
  return (
    <Card data-testid={`admin-order-${o.order_number}`}>
      <button onClick={() => setOpen(!open)} className="w-full px-5 py-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-left" data-testid={`admin-order-toggle-${o.order_number}`}>
        <span className="font-mono text-sm text-[#C85A32] w-32">{o.order_number}</span>
        <span className="text-xs font-mono text-stone-400 w-36">{fmtDate(o.created_at, true)}</span>
        <span className="text-sm text-stone-800 flex-1 min-w-[160px] truncate">{o.customer_name || o.customer_email || "—"}</span>
        <span className="text-xs text-stone-500 hidden md:block flex-1 truncate">{o.items.map((it) => `${it.name} ×${it.quantity}`).join(" · ")}</span>
        {o.type === "subscription" && <span className="rounded-full bg-[#4A624E]/10 text-[#4A624E] px-2.5 py-0.5 text-[10px] font-mono uppercase">Abo</span>}
        <span className="font-mono text-sm text-stone-900 w-24 text-right">{chf(o.total)}</span>
        <StatusBadge status={o.status} />
        {open ? <ChevronUp size={15} className="text-stone-400" /> : <ChevronDown size={15} className="text-stone-400" />}
      </button>
      {open && <OrderDetail o={o} onChanged={onChanged} />}
    </Card>
  );
};

export const Orders = ({ onChanged }) => {
  const [orders, setOrders] = useState(null);
  const [filters, setFilters] = useState({ status: "", q: "", month: "" });

  const load = useCallback(() => {
    get("/orders", Object.fromEntries(Object.entries(filters).filter(([, v]) => v))).then(setOrders).catch(() => setOrders([]));
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); onChanged(); };
  const total = (orders || []).filter((o) => !["cancelled", "refunded"].includes(o.status)).reduce((s, o) => s + o.total, 0);

  return (
    <div className="space-y-5" data-testid="admin-orders">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
          <input data-testid="orders-search" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="N° commande, client, email, suivi…" className={`${inputCls} w-full pl-9`} />
        </div>
        <select data-testid="orders-filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={inputCls}>
          <option value="">Tous les statuts</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <input data-testid="orders-filter-month" type="month" value={filters.month} max={monthKey()} onChange={(e) => setFilters({ ...filters, month: e.target.value })} className={inputCls} />
        {(filters.q || filters.status || filters.month) && <button onClick={() => setFilters({ status: "", q: "", month: "" })} className={btnLight}>Réinitialiser</button>}
      </div>
      <p className="text-xs font-mono text-stone-500" data-testid="orders-summary">{orders?.length ?? "…"} commande(s) · {chf(total)} (hors annulées/remboursées)</p>
      {orders === null ? <Empty>Chargement…</Empty> : orders.length === 0 ? <Empty>Aucune commande pour ces critères.</Empty> : (
        <div className="space-y-3">{orders.map((o) => <OrderRow key={o.id} o={o} onChanged={changed} />)}</div>
      )}
    </div>
  );
};
